import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TaskTypeResponse, TaskTypeRequest,
  TaskStatusResponse, TaskStatusRequest,
  TaskPriorityResponse, TaskPriorityRequest,
  TaskCategoryResponse, TaskCategoryRequest,
  TaskCancelReasonResponse, TaskCancelReasonRequest,
  PageResponse
} from '../models/task.model';

/**
 * Catálogos de Task. Expone:
 * - `/active` (sin paginación) — usado por el form de tareas y catálogos cacheados.
 * - CRUD paginado (list/getById/create/update/delete) — usado por las pantallas de admin/config.
 *
 * Multi-tenant: el backend deriva el `businessId` del JWT, no es necesario pasarlo en el body/query.
 * `DELETE` es soft-delete (isActive=false).
 */
@Injectable({ providedIn: 'root' })
export class TaskCatalogApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) {}

  // =====================================================
  // Active (no pagination) — para selects en task-form
  // =====================================================
  getActiveTypes(): Observable<TaskTypeResponse[]> {
    return this.http.get<TaskTypeResponse[]>(`${this.apiUrl}/task-types/active`);
  }
  getActiveStatuses(): Observable<TaskStatusResponse[]> {
    return this.http.get<TaskStatusResponse[]>(`${this.apiUrl}/task-statuses/active`);
  }
  getActivePriorities(): Observable<TaskPriorityResponse[]> {
    return this.http.get<TaskPriorityResponse[]>(`${this.apiUrl}/task-priorities/active`);
  }
  getActiveCategories(): Observable<TaskCategoryResponse[]> {
    return this.http.get<TaskCategoryResponse[]>(`${this.apiUrl}/task-categories/active`);
  }
  getActiveCancelReasons(): Observable<TaskCancelReasonResponse[]> {
    return this.http.get<TaskCancelReasonResponse[]>(`${this.apiUrl}/task-cancel-reasons/active`);
  }

  // =====================================================
  // Task Types CRUD
  // =====================================================
  listTypes(page = 0, size = 20, isActive?: boolean): Observable<PageResponse<TaskTypeResponse>> {
    return this.http.get<PageResponse<TaskTypeResponse>>(`${this.apiUrl}/task-types`, {
      params: this.buildParams(page, size, isActive)
    });
  }
  getTypeById(id: number): Observable<TaskTypeResponse> {
    return this.http.get<TaskTypeResponse>(`${this.apiUrl}/task-types/${id}`);
  }
  createType(req: TaskTypeRequest): Observable<TaskTypeResponse> {
    return this.http.post<TaskTypeResponse>(`${this.apiUrl}/task-types`, req);
  }
  updateType(id: number, req: TaskTypeRequest): Observable<TaskTypeResponse> {
    return this.http.put<TaskTypeResponse>(`${this.apiUrl}/task-types/${id}`, req);
  }
  deleteType(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/task-types/${id}`);
  }
  reorderTypes(businessId: number, orderedIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/task-types/reorder`, orderedIds, {
      params: this.reorderParams(businessId)
    });
  }

  // =====================================================
  // Task Statuses CRUD
  // =====================================================
  listStatuses(page = 0, size = 20, isActive?: boolean): Observable<PageResponse<TaskStatusResponse>> {
    return this.http.get<PageResponse<TaskStatusResponse>>(`${this.apiUrl}/task-statuses`, {
      params: this.buildParams(page, size, isActive)
    });
  }
  getStatusById(id: number): Observable<TaskStatusResponse> {
    return this.http.get<TaskStatusResponse>(`${this.apiUrl}/task-statuses/${id}`);
  }
  createStatus(req: TaskStatusRequest): Observable<TaskStatusResponse> {
    return this.http.post<TaskStatusResponse>(`${this.apiUrl}/task-statuses`, req);
  }
  updateStatus(id: number, req: TaskStatusRequest): Observable<TaskStatusResponse> {
    return this.http.put<TaskStatusResponse>(`${this.apiUrl}/task-statuses/${id}`, req);
  }
  deleteStatus(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/task-statuses/${id}`);
  }
  reorderStatuses(businessId: number, orderedIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/task-statuses/reorder`, orderedIds, {
      params: this.reorderParams(businessId)
    });
  }

  // =====================================================
  // Task Priorities CRUD
  // =====================================================
  listPriorities(page = 0, size = 20, isActive?: boolean): Observable<PageResponse<TaskPriorityResponse>> {
    return this.http.get<PageResponse<TaskPriorityResponse>>(`${this.apiUrl}/task-priorities`, {
      params: this.buildParams(page, size, isActive)
    });
  }
  getPriorityById(id: number): Observable<TaskPriorityResponse> {
    return this.http.get<TaskPriorityResponse>(`${this.apiUrl}/task-priorities/${id}`);
  }
  createPriority(req: TaskPriorityRequest): Observable<TaskPriorityResponse> {
    return this.http.post<TaskPriorityResponse>(`${this.apiUrl}/task-priorities`, req);
  }
  updatePriority(id: number, req: TaskPriorityRequest): Observable<TaskPriorityResponse> {
    return this.http.put<TaskPriorityResponse>(`${this.apiUrl}/task-priorities/${id}`, req);
  }
  deletePriority(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/task-priorities/${id}`);
  }

  // =====================================================
  // Task Categories CRUD
  // =====================================================
  listCategories(page = 0, size = 20, isActive?: boolean): Observable<PageResponse<TaskCategoryResponse>> {
    return this.http.get<PageResponse<TaskCategoryResponse>>(`${this.apiUrl}/task-categories`, {
      params: this.buildParams(page, size, isActive)
    });
  }
  getCategoryById(id: number): Observable<TaskCategoryResponse> {
    return this.http.get<TaskCategoryResponse>(`${this.apiUrl}/task-categories/${id}`);
  }
  createCategory(req: TaskCategoryRequest): Observable<TaskCategoryResponse> {
    return this.http.post<TaskCategoryResponse>(`${this.apiUrl}/task-categories`, req);
  }
  updateCategory(id: number, req: TaskCategoryRequest): Observable<TaskCategoryResponse> {
    return this.http.put<TaskCategoryResponse>(`${this.apiUrl}/task-categories/${id}`, req);
  }
  deleteCategory(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/task-categories/${id}`);
  }

  // =====================================================
  // Task Cancel Reasons CRUD
  // =====================================================
  listCancelReasons(page = 0, size = 20, isActive?: boolean): Observable<PageResponse<TaskCancelReasonResponse>> {
    return this.http.get<PageResponse<TaskCancelReasonResponse>>(`${this.apiUrl}/task-cancel-reasons`, {
      params: this.buildParams(page, size, isActive)
    });
  }
  getCancelReasonById(id: number): Observable<TaskCancelReasonResponse> {
    return this.http.get<TaskCancelReasonResponse>(`${this.apiUrl}/task-cancel-reasons/${id}`);
  }
  createCancelReason(req: TaskCancelReasonRequest): Observable<TaskCancelReasonResponse> {
    return this.http.post<TaskCancelReasonResponse>(`${this.apiUrl}/task-cancel-reasons`, req);
  }
  updateCancelReason(id: number, req: TaskCancelReasonRequest): Observable<TaskCancelReasonResponse> {
    return this.http.put<TaskCancelReasonResponse>(`${this.apiUrl}/task-cancel-reasons/${id}`, req);
  }
  deleteCancelReason(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/task-cancel-reasons/${id}`);
  }
  reorderCancelReasons(businessId: number, orderedIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/task-cancel-reasons/reorder`, orderedIds, {
      params: this.reorderParams(businessId)
    });
  }

  // =====================================================
  // Helpers
  // =====================================================
  private buildParams(page: number, size: number, isActive?: boolean): HttpParams {
    let p = new HttpParams().set('page', String(page)).set('size', String(size));
    if (isActive !== undefined) p = p.set('isActive', String(isActive));
    return p;
  }

  /**
   * A diferencia del resto de este servicio, los endpoints `/reorder` SÍ exigen `businessId`
   * explícito como query param; el llamador lo toma de AuthService.getCurrentUser().
   * Body: ids en el nuevo orden (lista COMPLETA del negocio) → 204 sin body.
   */
  private reorderParams(businessId: number): HttpParams {
    return new HttpParams().set('businessId', String(businessId));
  }
}
